/**
 * Profile API client — wraps `/v1/auth/me` GET/PATCH. Avatar upload is left
 * to the existing media upload flow; this module just persists the resulting
 * URL on the user row.
 */
import { API_BASE } from '@/src/infrastructure/lib/api';
import { auth } from '@/src/infrastructure/config/firebase';

export interface UserProfile {
  id: string;
  email: string;
  name: string;
  username: string | null;
  phone: string | null;
  avatar: string | null;
  provider: string | null;
  role: string;
  plan: string;
  // Mirrors `User.updatedAt` in the DB. Used as the cache-bust token on
  // hosted avatar URLs so the browser/CDN revalidates after an upload.
  updatedAt: string | null;
}

export interface UpdateProfileInput {
  name?: string;
  username?: string | null;
  email?: string;
  phone?: string | null;
  avatar?: string | null;
}

async function authedFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers as Record<string, string> | undefined),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg = Array.isArray(body?.message)
      ? body.message.join(', ')
      : body?.message ?? `HTTP ${res.status}`;
    throw new Error(msg);
  }
  if (res.status === 204) return undefined as T;
  const body = await res.json();
  return (body?.data !== undefined ? body.data : body) as T;
}

export const profileService = {
  me: () => authedFetch<UserProfile>('/auth/me'),
  update: (input: UpdateProfileInput) =>
    authedFetch<UserProfile>('/auth/me', {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
};
